import { useCallback, useEffect, useRef, useState } from "react";
import { getEcho, channelName, onConnectionStatus } from "../utils/echo";

const noop = () => {};

/**
 * Suscripción a un canal de broadcasting.
 *
 * Encapsula el ciclo completo: se suscribe al montar, se da de baja al
 * desmontar (o al cambiar de canal) y mantiene los handlers siempre
 * actualizados sin resuscribirse en cada render, que era lo que dejaba
 * listeners colgados al navegar entre salas.
 *
 * Soporta las tres visibilidades que usa la sala:
 *
 *  - `public`   canal abierto, sin autorización;
 *  - `private`  hilo entre dos personas, autorizado en /broadcasting/auth;
 *  - `presence` como el privado, y además publica quién está dentro. De ahí
 *               sale el aforo real de la transmisión.
 *
 * @param {string|null} name    Nombre lógico del canal (sin prefijo de Redis).
 * @param {object}      options
 * @param {string}      options.visibility 'public' | 'private' | 'presence'.
 * @param {object}      options.events     { NombreEvento: handler }.
 * @param {object}      options.presence   { here, joining, leaving }.
 * @param {object}      options.whispers   { nombre: handler } (eventos de cliente).
 */
export const useEchoChannel = (name, options = {}) => {
    const { visibility = 'public', events, presence, whispers } = options;

    const [status, setStatus] = useState('disconnected');
    const [subscribed, setSubscribed] = useState(false);
    const channelRef = useRef(null);

    // Los handlers viven en una ref para que un cambio de identidad de las
    // funciones no obligue a rehacer la suscripción en cada render.
    const handlersRef = useRef({});
    handlersRef.current = { events, presence, whispers };

    // Las claves de los eventos se congelan al montar la suscripción: añadir
    // un evento nuevo en caliente es un caso que no se da y evitarlo mantiene
    // la lista de dependencias honesta.
    const eventNames = Object.keys(events || {}).join(',');
    const whisperNames = Object.keys(whispers || {}).join(',');

    useEffect(() => onConnectionStatus(setStatus), []);

    useEffect(() => {
        if (!name) return undefined;

        const echo = getEcho();
        const fullName = channelName(name);

        const channel = visibility === 'presence'
            ? echo.join(fullName)
            : visibility === 'private'
                ? echo.private(fullName)
                : echo.channel(fullName);

        channelRef.current = channel;
        setSubscribed(true);

        eventNames.split(',').filter(Boolean).forEach((event) => {
            channel.listen(event, (payload) => {
                handlersRef.current.events?.[event]?.(payload);
            });
        });

        whisperNames.split(',').filter(Boolean).forEach((event) => {
            channel.listenForWhisper(event, (payload) => {
                handlersRef.current.whispers?.[event]?.(payload);
            });
        });

        if (visibility === 'presence') {
            channel
                .here((members) => handlersRef.current.presence?.here?.(members || []))
                .joining((member) => handlersRef.current.presence?.joining?.(member))
                .leaving((member) => handlersRef.current.presence?.leaving?.(member));
        }

        return () => {
            channelRef.current = null;
            setSubscribed(false);
            try {
                // `leave` cubre las tres variantes del nombre, así que sirve
                // igual para un canal público que para uno de presencia.
                echo.leave(fullName);
            } catch (error) {
                // Si el socket ya se cayó, no hay nada que limpiar.
            }
        };
    }, [name, visibility, eventNames, whisperNames]);

    /**
     * Evento de cliente: viaja entre navegadores sin pasar por el backend.
     * Solo lo aceptan los canales privados y de presencia.
     */
    const whisper = useCallback((event, payload) => {
        const channel = channelRef.current;
        if (!channel || typeof channel.whisper !== 'function') return;
        try {
            channel.whisper(event, payload);
        } catch (error) {
            // Un susurro perdido no debe romper el envío de mensajes.
        }
    }, []);

    return {
        status,
        connected: status === 'connected',
        subscribed,
        whisper: name ? whisper : noop,
    };
};
